import {AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {TabConfig} from '@core/types';
import {NgTemplateOutlet} from '@angular/common';

@Component({
  selector: 'core-tabs',
  imports: [
    NgTemplateOutlet
  ],
  standalone: true,
  templateUrl: './tabs.html',
  styleUrl: './tabs.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    style: 'display: flex;flex-direction: column;gap: 1rem;width: 100%;'
  }
})
export class Tabs implements OnInit, AfterViewInit {

  @Input()
  aligment: 'center' | 'left' | 'right' = 'left';
  @Input()
  labelTemplate?: TemplateRef<any>;
  @Input()
  header?: TemplateRef<any>;
  @Input()
  headerFull: boolean = false;
  @Input()
  keepVisitedAlive = true;
  @Input()
  set initialTab(value: string | null | undefined) {
    const normalized = value || '';
    if (normalized === this._initialTab) {
      return;
    }
    this._initialTab = normalized;
    this.syncCurrentTab();
  }
  @ViewChild('tabsContainer')
  tabsContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('contentContainer')
  contentContainer?: ElementRef<HTMLDivElement>;
  currentTab: string = '';
  canScrollLeft: boolean = false;
  canScrollRight: boolean = false;
  contentMinHeight = 0;
  @Output()
  onTabChange = new EventEmitter<string>();

  _config: TabConfig[] = [];
  private _initialTab = '';
  renderedTabs = new Set<string>();

  get config(): TabConfig[] {
    return this._config;
  }

  get currentTabConfig(): TabConfig | undefined {
    return this._config.find(tab => (tab.slug || '') === this.currentTab);
  }

  @Input()
  set config(config: TabConfig[]) {
    this._config = config || [];
    this.syncCurrentTab();
    this.onScroll();
  };

  ngOnInit() {
    this.syncCurrentTab();
    this.markCurrentTabAsRendered();
  }

  ngAfterViewInit() {
    this.captureContentHeight();
  }

  changeTab(tab: TabConfig) {
    if (tab.disabled) return;
    const slug = tab.slug || '';
    this.currentTab = slug;
    this.markCurrentTabAsRendered();
    this.onTabChange.emit(slug);
    this.captureContentHeight();
  }

  scrollTabs(direction: 'left' | 'right') {
    const container = this.tabsContainer?.nativeElement;
    if (!container) return;

    const scrollAmount = 200;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  }

  onScroll() {
    const container = this.tabsContainer?.nativeElement;
    if (!container) return;

    this.canScrollLeft = container.scrollLeft > 0;
    this.canScrollRight = container.scrollLeft < (container.scrollWidth - container.clientWidth);
  }

  private syncCurrentTab() {
    const available = this._config.filter(tab => !tab.disabled);
    const requestedInitialTab = available.find(tab => tab.slug === this._initialTab);
    if (requestedInitialTab && this.currentTab !== requestedInitialTab.slug) {
      this.currentTab = requestedInitialTab.slug || '';
    }
    const current = this._config.find(tab => tab.slug === this.currentTab);
    const isCurrentDisabled = current?.disabled;
    const isCurrentMissing = !current;
    if ((!this.currentTab && requestedInitialTab) || isCurrentDisabled || isCurrentMissing) {
      this.currentTab = requestedInitialTab?.slug || '';
    }
    if (!this.currentTab || isCurrentDisabled || isCurrentMissing) {
      this.currentTab = available.length ? (available[0].slug || '') : '';
    }
    this.markCurrentTabAsRendered();
    this.captureContentHeight();
  }

  isTabRendered(slug?: string): boolean {
    const normalizedSlug = slug || '';
    if (!normalizedSlug) {
      return false;
    }

    return this.keepVisitedAlive
      ? this.renderedTabs.has(normalizedSlug)
      : this.currentTab === normalizedSlug;
  }

  private markCurrentTabAsRendered() {
    if (!this.currentTab) {
      return;
    }

    this.renderedTabs.add(this.currentTab);
  }

  private captureContentHeight() {
    requestAnimationFrame(() => {
      const container = this.contentContainer?.nativeElement;
      if (!container) return;

      const panel = container.firstElementChild as HTMLElement | null;
      const panelHeight = panel?.offsetHeight || 0;
      if (panelHeight > this.contentMinHeight) {
        this.contentMinHeight = panelHeight;
      }
    });
  }
}
